import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { backgroundProcessApi } from '../services/api';
import { BackgroundProcess } from '../types';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { appDateValue, formatAppDateTime } from '../utils/dateFormat';

// Новый тип для последних успешных запусков
interface LastCompletedProcess {
  code: string;
  description: string;
  process: BackgroundProcess | null;
}

const Dashboard: React.FC = () => {
  const [backgroundProcesses, setBackgroundProcesses] = useState<BackgroundProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof BackgroundProcess>('startedTs');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterProcess, setFilterProcess] = useState<string>('');
  const [lastCompleted, setLastCompleted] = useState<LastCompletedProcess[]>([]);

  useEffect(() => {
    loadBackgroundProcesses();
    loadLastCompleted();
  }, []);

  const getRowBgColor = (process: BackgroundProcess) => {
    const status = process.statusCd;
    if (status === 'FAILED') return (theme: Theme) => alpha(theme.palette.error.light, 0.3);
    if (status === 'COMPLETED') return (theme: Theme) => alpha(theme.palette.success.light, 0.3);
    if (status === 'SKIP') return (theme: Theme) => alpha(theme.palette.grey[300], 0.5);
    return undefined;
  };

  const loadBackgroundProcesses = async () => {
    try {
      setLoading(true);
      const response = await backgroundProcessApi.getLatest();
      setBackgroundProcesses(response);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки данных');
      setBackgroundProcesses([]);
      console.error('Error loading background processes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLastCompleted = async () => {
    try {
      const data = await backgroundProcessApi.getLastCompleted();
      setLastCompleted(data || []);
    } catch (err) {
      setLastCompleted([]);
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTime: string) =>
    formatAppDateTime(dateTime, true, '-', true);

  const getProcessDisplayName = (processCode: string) => {
    switch (processCode) {
      case 'VIDEO_COLLECTION':
        return 'Сбор видео (все платформы)';
      case 'VK_VIDEO_SYNC':
        return 'Синхронизация VK Video';
      case 'YOUTUBE_SYNC':
        return 'Синхронизация YouTube';
      case 'STATS_COLLECTION':
        return 'Сбор статистики';
      case 'VKORD_SEND':
        return 'Отправка статистики в VkOrd';
      case 'vk-video-sync':
        return 'Синхронизация VK Video';
      case 'youtube-sync':
        return 'Синхронизация YouTube';
      case 'vkord-sync':
        return 'Отправка статистики в VkOrd';
      default:
        return processCode;
    }
  };

  const handleSort = (property: typeof orderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const processOptions = Array.from(new Set(backgroundProcesses.map(p => getProcessDisplayName(p.processCd))));
  const statusOptions = Array.from(new Set(backgroundProcesses.map(p => p.statusCd)));

  const filtered = backgroundProcesses.filter(p =>
    (!filterStatus || (p.statusCd ? p.statusCd.toLowerCase().includes(filterStatus.toLowerCase()) : false)) &&
    (!filterProcess || (getProcessDisplayName(p.processCd) ? getProcessDisplayName(p.processCd).toLowerCase().includes(filterProcess.toLowerCase()) : false))
  );
  const sorted = filtered.sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    if (orderBy === 'startedTs' || orderBy === 'endedTs') {
      const aNum = aValue ? appDateValue(aValue as string) : 0;
      const bNum = bValue ? appDateValue(bValue as string) : 0;
      if (aNum < bNum) return order === 'asc' ? -1 : 1;
      if (aNum > bNum) return order === 'asc' ? 1 : -1;
      return 0;
    } else {
      const aStr = aValue ? String(aValue) : '';
      const bStr = bValue ? String(bValue) : '';
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    }
  });
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        Панель управления
      </Typography>
      
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Последние успешные запуски по типам процессов */}
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Последние успешные запуски процессов
            </Typography>
            {Array.isArray(lastCompleted) && lastCompleted.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Тип процесса</TableCell>
                      <TableCell>Время запуска</TableCell>
                      <TableCell>Время завершения</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lastCompleted.map(({ code, description, process }) => (
                      <TableRow key={code}>
                        <TableCell>{description}</TableCell>
                        <TableCell>{process?.startedTs ? formatDateTime(process.startedTs) : <span style={{ color: '#aaa' }}>Нет запусков</span>}</TableCell>
                        <TableCell>{process?.endedTs ? formatDateTime(process.endedTs) : <span style={{ color: '#aaa' }}>—</span>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                Нет данных о последних успешных запусках процессов
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard; 